import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FormControl, Select, MenuItem, Box, Typography, Alert, LinearProgress } from '@mui/material';
import { apiGet } from '../utils/api';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import '../styles/ChatWithLogs.css';
import logger from '../utils/logger';

export default function ChatWithLogs() {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [indices, setIndices] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState('');
  const [eventSource, setEventSource] = useState(null);
  const [indexMapping, setIndexMapping] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [toast, setToast] = useState({ message: '', show: false, type: 'success' });
  const [status, setStatus] = useState({ status: '', message: '' });
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Log status changes
    if (status.status) {
      logger.debug('Status state changed', { 
        statusType: status.status,
        statusMessage: status.message
      });
    }
  }, [status]);

  useEffect(() => {
    // Generate a unique session ID when the component first loads
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);
    
    const fetchIndices = async () => {
      try {
        const response = await apiGet('/chat/indices');
        // Filter indices to only include those starting with "opensearch_dashboards_sample"
        const filteredIndices = response.filter(idx => 
          idx.index.startsWith('opensearch_dashboards_sample')
        );
        setIndices(filteredIndices);
        logger.info('Fetched indices', { count: filteredIndices.length });
      } catch (err) {
        logger.error('Error fetching indices:', { error: err.message, stack: err.stack });
        setError('Failed to fetch indices. Please try again later.');
      }
    };

    fetchIndices();
    return () => cleanupEventSource();
  }, []);

  useEffect(() => {
    const fetchMapping = async () => {
      if (!selectedIndex) {
        setIndexMapping(null);
        return;
      }

      try {
        const mapping = await apiGet(`/chat/mapping/${selectedIndex}`);
        setIndexMapping(mapping);
        logger.info('Fetched mapping for index', { index: selectedIndex });
      } catch (err) {
        logger.error('Error fetching index mapping:', { error: err.message, stack: err.stack });
        setError('Failed to fetch index mapping. Please try again later.');
      }
    };

    fetchMapping();
  }, [selectedIndex]);

  const cleanupEventSource = () => {
    if (eventSource) {
      // No need to close the event source in this case
      setEventSource(null);
    }
  };

  const setupEventSource = (url, token) => {
    try {
      // Close any existing EventSource
      cleanupEventSource();
      
      // We can't add headers to native EventSource, so we need to use a custom implementation
      // or make a fetch request that handles SSE manually
      logger.debug('Initiating chat stream request', { url, sessionId });
      
      const fetchSSE = async () => {
        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'text/event-stream',
              'Cache-Control': 'no-cache'
            }
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            logger.error('Chat stream request failed', { 
              status: response.status, 
              statusText: response.statusText,
              response: errorText,
              sessionId
            });
            throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
          }
          
          // Initialize assistant message
          let assistantMessage = null;
          
          // Handle the stream manually
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          
          while (true) {
            const { value, done } = await reader.read();
            
            if (done) {
              setIsGenerating(false);
              logger.debug('Stream completed', { sessionId });
              break;
            }
            
            buffer += decoder.decode(value, { stream: true });
            
            // Process complete events from the buffer
            const events = buffer.split('\n\n');
            
            for (let i = 0; i < events.length - 1; i++) {
              const event = events[i];
              const lines = event.split('\n');
              const eventType = lines[0]?.replace('event: ', '');
              const data = lines[1]?.replace('data: ', '');
              
              if (!eventType || !data) continue;
              
              if (eventType === 'connect') {
                logger.debug('Connected to SSE stream', { sessionId });
              } else if (eventType === 'status') {
                try {
                  const statusData = JSON.parse(data);
                  console.log('COMPLETE STATUS EVENT:', statusData); // Debug log
                  logger.debug('Received status event', { 
                    sessionId, 
                    statusType: statusData.status,
                    statusMessage: statusData.message
                  });
                  setStatus(statusData);
                  
                  if (statusData.status === 'complete') {
                    setIsGenerating(false);
                    logger.debug('Received complete status', { 
                      sessionId, 
                      status: statusData.status,
                      hasMetadata: !!statusData.metadata
                    });
                    
                    // If metadata contains query and query results, add them to messages
                    if (statusData.metadata && (statusData.metadata.query || statusData.metadata.queryResults)) {
                      //console.log('METADATA FOUND:', statusData.metadata); // Debug log
                      // Add a system message with the query and results
                      const metadataMessage = {
                        role: 'system',
                        content: '',
                        isExpanded: false, // Set initially collapsed
                        metadata: {
                          query: statusData.metadata.query,
                          queryResults: statusData.metadata.queryResults,
                          sessionType: statusData.metadata.sessionType
                        }
                      };
                      //console.log('ADDING METADATA MESSAGE:', metadataMessage); // Debug log
                      setMessages(prev => {
                        //console.log('PREVIOUS MESSAGES:', prev); // Debug log
                        const newMessages = [...prev, metadataMessage];
                        //console.log('NEW MESSAGES:', newMessages); // Debug log
                        return newMessages;
                      });
                      logger.debug('Added metadata message', { 
                        sessionId, 
                        hasQuery: !!statusData.metadata.query,
                        hasResults: !!statusData.metadata.queryResults
                      });
                    }
                  }
                } catch (error) {
                  logger.error('Error parsing status data', { error, data, sessionId });
                }
              } else if (eventType === 'trace') {
                try {
                  const traceData = JSON.parse(data);
                  
                  if (!assistantMessage) {
                    // First chunk, add to messages
                    assistantMessage = { role: 'assistant', content: traceData.content };
                    setMessages(prev => [...prev, assistantMessage]);
                    logger.debug('Received first trace chunk', { 
                      sessionId, 
                      contentLength: traceData.content.length 
                    });
                  } else {
                    // Update existing message
                    assistantMessage.content += traceData.content;
                    setMessages(prev => [...prev.slice(0, -1), { ...assistantMessage }]);
                  }
                  
                  // Scroll to bottom
                  if (messagesEndRef.current) {
                    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                  }
                } catch (error) {
                  logger.error('Error parsing trace data', { error, data, sessionId });
                }
              } else if (eventType === 'error') {
                try {
                  const errorData = JSON.parse(data);
                  setError(errorData.error || 'Error processing request');
                  setIsGenerating(false);
                  logger.error('Received error event', { error: errorData.error, sessionId });
                } catch (error) {
                  logger.error('Error parsing error data', { error, data, sessionId });
                  setError('Error processing response');
                  setIsGenerating(false);
                }
              } else if (eventType === 'sessionId') {
                try {
                  const sessionData = JSON.parse(data);
                  if (sessionData.sessionId) {
                    setSessionId(sessionData.sessionId);
                    logger.debug('Received session ID from server', { sessionId: sessionData.sessionId });
                  }
                } catch (error) {
                  logger.error('Error parsing sessionId data', { error, data, sessionId });
                }
              }
            }
            
            // Keep the last incomplete event in the buffer
            buffer = events[events.length - 1];
          }
        } catch (error) {
          logger.error('Error in SSE stream', { 
            error: error.message, 
            stack: error.stack,
            sessionId
          });
          setError(error.message || 'Error connecting to chat service');
          setIsGenerating(false);
        }
      };
      
      fetchSSE();
    } catch (err) {
      logger.error('Error setting up EventSource:', { 
        error: err.message, 
        stack: err.stack,
        sessionId
      });
      setError(err.message || 'An error occurred while connecting to the chat service');
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !selectedIndex || isGenerating) return;

    setIsGenerating(true);
    setError(null);

    // Add user message to the chat
    const userMessageObj = { role: 'user', content: inputMessage };
    setMessages(prevMessages => [...prevMessages, userMessageObj]);
    setInputMessage('');

    try {
      // Set up query parameters
      const queryParams = new URLSearchParams();
      
      // Add required parameters
      if (!selectedIndex) {
        throw new Error('Please select an index first');
      }
      queryParams.append('index', selectedIndex);
      queryParams.append('message', inputMessage);
      queryParams.append('sessionId', sessionId);
      
      // Get the auth token from localStorage (stored during login)
      const idToken = localStorage.getItem('idToken');
      
      if (!idToken) {
        throw new Error('Authentication token not available. Please log in again.');
      }
      
      // Set up event source
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const eventSourceUrl = `${API_URL}/chat/stream?${queryParams.toString()}`;
      
      logger.info('[CLIENT] user asked a question', { 
        sessionId, 
        index: selectedIndex, 
        messageLength: inputMessage.length,
        userEmail: currentUser?.email || 'anonymous',
        userInput: inputMessage
      });
      
      setupEventSource(eventSourceUrl, idToken);
      
    } catch (err) {
      logger.error('Error in chat submission:', { 
        error: err.message, 
        stack: err.stack,
        sessionId
      });
      setError(err.message || 'An error occurred while sending your message');
      setIsGenerating(false);
    }
  };

  // Helper to get status color based on status type
  const getStatusColor = (status) => {
    switch (status) {
      case 'error':
        return '#f44336'; // red
      case 'complete':
      case 'completed':
      case 'metrics':
        return '#4caf50'; // green
      case 'streaming':
      case 'generating':
        return '#2196f3'; // blue
      case 'processing':
      case 'receiving':
      case 'thinking':
      case 'finalizing':
        return '#ff9800'; // orange
      case 'initializing':
      case 'preparing':
      case 'connecting':
        return '#9c27b0'; // purple
      default:
        return '#9e9e9e'; // grey
    }
  };

  return (
    <div className="chat-with-logs-container">
      <div className="chat-header">
        <h2>Chat with Logs</h2>
        {sessionId && (
          <div className="session-id-display">
            Session ID: <span className="session-id">{sessionId.split('_')[1]}</span>
          </div>
        )}
      </div>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            marginBottom: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              flexDirection: 'column'
            }
          }}
          onClose={() => setError(null)}
        >
          <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold' }}>
            Error
          </Typography>
          <Typography variant="body2" component="div">
            {error}
          </Typography>
        </Alert>
      )}

      {toast.show && (
        <Alert severity={toast.type} className="toast">
          {toast.message}
        </Alert>
      )}

      <div className="chat-content">
        <div className="chat-messages">
          {messages.length === 0 && (
            <div className="placeholder-message">
              <div className="placeholder-content">
                <h3>Chat with your OpenSearch logs</h3>
                <p>Select an index and ask questions about your logs such as:</p>
                <ul>
                  <li>"Show me error logs from the last hour"</li>
                  <li>"Find all failed transactions with status code 500"</li>
                  <li>"What are the most common errors in my application?"</li>
                  <li>"Show me logs related to user authentication"</li>
                </ul>
              </div>
            </div>
          )}
          {messages.map((message, index) => {
            const isUser = message.role === 'user';
            const isSystem = message.role === 'system';
            // Handle both string content and object content formats
            const messageContent = typeof message.content === 'string' 
              ? message.content 
              : message.content[0]?.text || '';
            
            // console.log(`Rendering message ${index}:`, { 
            //   role: message.role, 
            //   isSystem, 
            //   hasMetadata: isSystem && !!message.metadata,
            //   metadata: isSystem ? message.metadata : null
            // });
            
            // If it's a system message with metadata, render it differently
            if (isSystem && message.metadata) {
             // console.log('Rendering system message with metadata:', message);
              return (
                <div key={index} className="system-message metadata-message">
                  <div className="message-header">
                    <span className="message-role">OpenSearch Query and Response</span>
                    <button 
                      className="toggle-button"
                      onClick={() => {
                        // Toggle visibility of this specific metadata message
                        setMessages(prev => prev.map((msg, i) => 
                          i === index ? {...msg, isExpanded: !msg.isExpanded} : msg
                        ));
                      }}
                    >
                      {message.isExpanded ? 'Hide' : 'Show'} OpenSearch Query and Response
                    </button>
                  </div>
                  
                  {message.isExpanded && (
                    <div className="metadata-content">
                      {message.metadata.query && (
                        <div className="query-section">
                          <h4>OpenSearch Query</h4>
                          <JsonView 
                            src={(() => {
                              try {
                                // If it's already an object, use it directly
                                if (typeof message.metadata.query === 'object' && message.metadata.query !== null) {
                                  return message.metadata.query;
                                }
                                // If it's a string, try to parse it as JSON
                                if (typeof message.metadata.query === 'string') {
                                  try {
                                    return JSON.parse(message.metadata.query);
                                  } catch (e) {
                                    // If parsing fails, return it as a simple string object
                                    console.log('Error parsing query as JSON:', e);
                                    return { text: message.metadata.query };
                                  }
                                }
                                // Fallback for any other type
                                return { value: String(message.metadata.query) };
                              } catch (error) {
                                console.error('Error processing query data:', error);
                                return { error: 'Could not process query data' };
                              }
                            })()}
                            collapsed={false}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            enableClipboard={true}
                            theme="light"
                          />
                        </div>
                      )}
                      
                      {message.metadata.queryResults && (
                        <div className="results-section">
                          <h4>OpenSearch Query Results</h4>
                          <JsonView 
                            src={(() => {
                              try {
                                // If it's already an object, use it directly
                                if (typeof message.metadata.queryResults === 'object' && message.metadata.queryResults !== null) {
                                  return message.metadata.queryResults;
                                }
                                // If it's a string, try to parse it as JSON
                                if (typeof message.metadata.queryResults === 'string') {
                                  try {
                                    return JSON.parse(message.metadata.queryResults);
                                  } catch (e) {
                                    // If parsing fails, return it as a simple string object
                                    console.log('Error parsing query results as JSON:', e);
                                    return { text: message.metadata.queryResults };
                                  }
                                }
                                // Fallback for any other type
                                return { value: String(message.metadata.queryResults) };
                              } catch (error) {
                                console.error('Error processing query results data:', error);
                                return { error: 'Could not process query results data' };
                              }
                            })()} 
                            collapsed={2}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            enableClipboard={true}
                            theme="light"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <div
                key={index}
                className={`message ${isUser ? 'user-message' : 'assistant-message'}`}
              >
                <div className="message-header">
                  <span className="message-role">{isUser ? 'You' : 'Assistant'}</span>
                  {!isUser && status.status === 'completed' && (
                    <button 
                      className="copy-button" 
                      onClick={() => {
                        navigator.clipboard.writeText(messageContent);
                        setToast({
                          show: true,
                          type: 'success',
                          message: 'Response copied to clipboard'
                        });
                        setTimeout(() => setToast({ ...toast, show: false }), 3000);
                      }}
                      title="Copy response"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  )}
                </div>
                <div className="message-content">{messageContent}</div>
              </div>
            );
          })}
          {isGenerating && (
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="chat-footer">
        {/* Status indicator */}
        {status.status && (
          <div className="status-indicator">
            <div 
              className="status-badge" 
              style={{ backgroundColor: getStatusColor(status.status) }}
            ></div>
            <span className="status-message">
              <strong>{status.status.charAt(0).toUpperCase() + status.status.slice(1)}:</strong> {status.message}
            </span>
            {['processing', 'streaming', 'receiving', 'thinking', 'generating', 'finalizing'].includes(status.status) && (
              <LinearProgress 
                sx={{ 
                  width: '100%', 
                  height: 4, 
                  marginTop: '4px',
                  borderRadius: '2px',
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getStatusColor(status.status)
                  }
                }} 
              />
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="chat-input-form">
          <FormControl variant="outlined" size="small" sx={{ 
            minWidth: 200,
            marginRight: 1,
            '& .MuiOutlinedInput-root': {
              height: 40,
              backgroundColor: 'white',
              borderRadius: '4px',
              border: '1px solid #ccc',
              '& fieldset': {
                border: 'none'
              }
            },
            '& .MuiSelect-select': {
              paddingTop: '8.5px',
              paddingBottom: '8.5px',
              fontSize: '0.875rem'
            }
          }}>
            <Select
              value={selectedIndex}
              onChange={(e) => {
                logger.info('Selected index', { index: e.target.value });
                setSelectedIndex(e.target.value);
              }}
              disabled={isGenerating}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      fontSize: '0.875rem',
                      minHeight: 36
                    }
                  }
                }
              }}
            >
              <MenuItem value="" disabled>Select an index</MenuItem>
              {indices.map((idx) => (
                <MenuItem key={idx.index} value={idx.index}>
                  {idx.index}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <input
            type="text"
            className="chat-input"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={selectedIndex ? "Type your message..." : "Please select an index to start chatting"}
            disabled={!selectedIndex || isGenerating}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <button 
            type="submit" 
            className="send-button"
            disabled={!selectedIndex || isGenerating || !inputMessage.trim()}
            aria-label="Send message"
          >
            <span className="send-text">Send</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}
